import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  makeStyles,
  withStyles,
  Stepper as MaterialStepper,
  Step,
  StepLabel,
  StepConnector,
  StepContent,
  Box,
  Typography,
  LinearProgress,
  useTheme,
} from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import TranslatedText from './TranslatedText';

const useStyles = makeStyles((theme) => ({
  stepperContainer: {
    width: '100%',
    boxSizing: 'border-box',
    maxWidth: 1200,
    padding: `0 ${theme.spacing(2)}px`,
  },
  stepperHorizontal: {
    padding: 0,
  },
  bar1Determinate: {
    transition: 'transform .1s linear',
  },
  stepHorizontal: {
    padding: 0,
  },
  stepLabelContainerHorizontal: {
    fontSize: 16,
    padding: `${theme.spacing(2)}px 0`,
    color: theme.palette.text.disabled,
  },
  stepLabelContainerActiveHorizontal: {
    color: theme.palette.primary.main,
  },
  linearProgressHorizontal: {
    left: '-100%',
    top: 25,
    transition: 'none',
    position: 'absolute',
  },
  stepContentRootVertical: {
    paddingLeft: 54,
    marginTop: 0,
    marginLeft: 24,
    borderLeft: `2px solid ${theme.palette.text.disabled}`,
  },
  stepContentRootLastVertical: {
    border: 'none',
  },
  stepLabelContainerVertical: {
    paddingLeft: 20,
  },
  stepLabelVertical: {
    color: theme.palette.text.disabled,
  },
  stepLabelActiveVertical: {
    color: theme.palette.primary.main,
  },
  stepDescriptionVertical: {
    color: theme.palette.text.disabled,
  },
  stepDescriptionHorizontal: {
    color: theme.palette.text.disabled,
    fontSize: 12,
    lineHeight: '22px',
  },
  stepDescriptionActiveHorizontal: {
    color: theme.palette.text.hint,
  },
}));

const useStepIconStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.common.white,
    boxSizing: 'border-box',
    border: `2px solid ${theme.palette.text.disabled}`,
    zIndex: 1,
    color: theme.palette.text.disabled,
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 26,
    fontWeight: 'bold',
  },
  active: {
    border: 'none',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.secondary.main,
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  },
  completed: {
    border: 'none',
    color: theme.palette.common.white,
    backgroundColor: theme.palette.secondary.main,
  },
}));

const StepIcon = ({ active, completed, icon }) => {
  const classes = useStepIconStyles();
  return (
    <div
      className={cn(classes.root, {
        [classes.active]: active,
        [classes.completed]: completed,
      })}
    >
      {icon}
    </div>
  );
};

StepIcon.propTypes = {
  active: PropTypes.bool.isRequired,
  completed: PropTypes.bool.isRequired,
  icon: PropTypes.number.isRequired,
};

const Connector = ({
  index,
  classes,
  progress,
  stepsLength,
}) => (index === 1 ? (
  <LinearProgress
    className={classes.linearProgressHorizontal}
    style={{ width: `${stepsLength * 100}%` }}
    variant="determinate"
    color="secondary"
    value={progress}
    classes={{ bar1Determinate: classes.bar1Determinate }}
  />
) : null);

Connector.propTypes = {
  index: PropTypes.number.isRequired,
  classes: PropTypes.shape({
    linearProgressHorizontal: PropTypes.string,
    bar1Determinate: PropTypes.string,
  }).isRequired,
  progress: PropTypes.number.isRequired,
  stepsLength: PropTypes.number.isRequired,
};

const ConnectorBlank = ({ index, classes, stepsLength }) => (index === 1 ? (
  <LinearProgress
    className={classes.linearProgressHorizontal}
    style={{ width: `${stepsLength * 100}%` }}
    variant="determinate"
    color="secondary"
    value={0}
  />
) : null);

ConnectorBlank.propTypes = {
  index: PropTypes.number.isRequired,
  classes: PropTypes.shape({
    linearProgressHorizontal: PropTypes.string,
    bar1Determinate: PropTypes.string,
  }).isRequired,
  stepsLength: PropTypes.number.isRequired,
};

const VerticalConnector = withStyles((theme) => ({
  active: {
    '& $line': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  completed: {
    '& $line': {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}))(StepConnector);

const Stepper = ({ steps }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = React.useState(0);
  const theme = useTheme();
  const isVertical = !useMediaQuery(theme.breakpoints.up('md'));
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        return Math.min(oldProgress + 1, 100);
      });
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (progress === 0) setActiveStep(-1);
    else {
      steps.forEach((step, i) => {
        const stepProgress = ((100 / steps.length) / 2) + (100 / steps.length) * i;
        if (progress >= stepProgress) { setActiveStep(i); }
      });
    }
  }, [progress, steps]);

  if (isVertical) {
    return (
      <MaterialStepper
        activeStep={activeStep}
        orientation="vertical"
        connector={VerticalConnector}
      >
        {steps.map(({ id, label, description }, i) => {
          const isStepActive = activeStep >= i;
          return (
            <Step key={id}>
              <StepLabel
                classes={{ labelContainer: classes.stepLabelContainerVertical }}
                StepIconComponent={StepIcon}
              >
                <Typography
                  className={cn(classes.stepLabelVertical,
                    { [classes.stepLabelActiveVertical]: isStepActive })}
                  variant="h6"
                >
                  <TranslatedText id={label} />
                </Typography>
              </StepLabel>
              <StepContent
                classes={{ root: classes.stepContentRootVertical, last: classes.stepContentRootLastVertical }}
                TransitionComponent="div"
              >
                <Box pb={4} pt={1} clone>
                  <Typography variant="body2" className={!isStepActive && classes.stepDescriptionVertical}>
                    <TranslatedText id={description} />
                  </Typography>
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </MaterialStepper>
    );
  }

  return (
    <Box className={classes.stepperContainer}>
      <MaterialStepper
        className={classes.stepperHorizontal}
        alternativeLabel
        activeStep={activeStep}
        connector={progress === 0 ? (
          <ConnectorBlank progress={progress} activeStep={activeStep} classes={classes} stepsLength={steps.length} />
        ) : (
          <Connector progress={progress} activeStep={activeStep} classes={classes} stepsLength={steps.length} />
        )}
      >
        {steps.map(({ id, label, description }, i) => {
          const isStepActive = activeStep >= i;
          return (
            <Step key={id} classes={{ horizontal: classes.stepHorizontal }}>
              <StepLabel StepIconComponent={StepIcon}>
                <Box
                  className={cn(classes.stepLabelContainerHorizontal,
                    { [classes.stepLabelContainerActiveHorizontal]: isStepActive })}
                >
                  <TranslatedText id={label} />
                </Box>
                <Box px={1}>
                  <Typography
                    className={cn(classes.stepDescriptionHorizontal,
                      { [classes.stepDescriptionActiveHorizontal]: isStepActive })}
                  >
                    <TranslatedText id={description} />
                  </Typography>
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </MaterialStepper>
    </Box>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default Stepper;
